import { useState } from 'react';
import { z } from 'zod';
import { useSteppedFormComponents, useSteppedFormControls } from '@mews-commander/form';
import { createOption, EnterpriseLinkButton, Loader } from '@mews-commander/ui';
import { PageFooter } from '@mews/b2b-ui';
import { createZodFormResolver, lt } from '@mews/framework';
import { Container, Stack } from '@optimus-web/core';
import { ReservationContractTemplateAddParameters, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { useGetServiceNamesByIdQuery } from '../common/queries/useGetServiceNamesByIdQuery';
import { useExistingTemplateNames } from '../externalTemplates/templatesTable/hooks/useExistingTemplateNames';
import { TemplateOptionsForm, templateOptionsSchema } from './TemplatesOptionsForm';
import { createDataMappingSchema, DataMappingForm } from './dataMapping/DataMappingForm';
import { TemplateFormStepFields } from './dataMapping/constants';
import { TemplateFormValues } from './types';
import { useAddTemplateMutation } from './hooks/useAddTemplateMutation';
import { removeFieldPrefix } from './utils';
import { useContractFieldsQuery } from './hooks/useContractFieldsQuery';

enum TemplateFormSteps {
    TemplateOptions = 'templateOptions',
    DataMapping = 'dataMapping',
}
const templateFormSteps = [TemplateFormSteps.TemplateOptions, TemplateFormSteps.DataMapping] as const;

interface TemplateFormProps {
    initialValues?: TemplateFormValues;
}

export const TemplateForm = ({ initialValues }: TemplateFormProps) => {
    const [externalTemplateId, setExternalTemplateId] = useState<string | undefined>(undefined);
    const existingTemplateNames = useExistingTemplateNames();
    const { StepProgressIndicator, SteppedPrimaryActions, state } = useSteppedFormComponents({
        steps: templateFormSteps,
    });
    const { mutateAsync: addTemplateMutation, isPending: isAddMutationPending } = useAddTemplateMutation();

    const { data: contractFieldsData, isPending: isContractFieldsPending } = useContractFieldsQuery({
        externalTemplateId,
    });
    const dataMappingFields = contractFieldsData?.documentFields?.[0]?.fields;

    const { renderFormContent, submit } = useSteppedFormControls<TemplateFormSteps, TemplateFormValues>({
        ...state,
        steps: templateFormSteps,
        stepResolvers: {
            templateOptions: createZodFormResolver(
                z.object({
                    [TemplateFormStepFields.templateOptionsFields]: templateOptionsSchema(existingTemplateNames),
                })
            ),
            dataMapping: createZodFormResolver(
                z.object({
                    [TemplateFormStepFields.dataMappingFields]:
                        dataMappingFields == null
                            ? z.object({})
                            : createDataMappingSchema({ fieldsData: dataMappingFields }),
                })
            ),
        },
        stepHandlers: {
            templateOptions: (values, { setCurrentStep }) => {
                const templateId = values.templateOptionsFields.template;
                setExternalTemplateId(templateId);
                setCurrentStep(TemplateFormSteps.DataMapping);
            },
        },
        onSubmit: async (values) => {
            const parameters: ReservationContractTemplateAddParameters = {
                externalTemplateId: values.templateOptionsFields.template,
                name: values.templateOptionsFields.name,
                description: values.templateOptionsFields.description,
                serviceIds: values.templateOptionsFields.services,
                fieldMappings: Object.entries(values.dataMappingFields).map(([id, value]) => ({
                    externalFieldId: removeFieldPrefix(id),
                    identifier: value,
                })),
            };
            await addTemplateMutation(parameters);
            return true;
        },
        values: initialValues,
    });

    const { data: serviceNamesById } = useGetServiceNamesByIdQuery();
    const serviceOptions = Object.entries(serviceNamesById).map(([id, name]) =>
        createOption({
            value: id,
            label: name,
        })
    );

    const { data: contractExternalTemplatesData } = useRepositorySuspenseQuery(
        'reservationContractExternalTemplates',
        'getAll',
        {}
    );
    const templateOptions =
        contractExternalTemplatesData.templates?.map((template) =>
            createOption({
                value: template.id,
                label: template.name,
            })
        ) ?? [];

    return (
        <Container padding={['0', '0', '1200', '0']}>
            <Stack vertical itemAlignment="center" spacing="400">
                <Container maxWidth="600px" width="100%">
                    <StepProgressIndicator
                        stepLabels={{
                            templateOptions: lt('ContractTemplateOptionsStepLabel'),
                            dataMapping: lt('ContractTemplateDataMappingStepLabel'),
                        }}
                    />
                </Container>
                <Container maxWidth="600px" width="100%">
                    {renderFormContent(({ matchSteps }) =>
                        matchSteps({
                            templateOptions: () => (
                                <TemplateOptionsForm
                                    serviceOptions={serviceOptions}
                                    templateOptions={templateOptions}
                                />
                            ),
                            dataMapping: () => (isContractFieldsPending ? <Loader /> : <DataMappingForm />),
                        })
                    )}
                </Container>
            </Stack>
            <PageFooter>
                <EnterpriseLinkButton to="Settings/Contracts" variant="tertiary">
                    {lt('Cancel')}
                </EnterpriseLinkButton>
                <SteppedPrimaryActions disabled={isAddMutationPending} handleSubmit={submit} />
            </PageFooter>
        </Container>
    );
};
